import { AllEffect, CallEffect, ForkEffect, GetContextEffect, PutEffect, SelectEffect } from 'redux-saga/effects';
import { buffers, eventChannel, EventChannel } from 'redux-saga';
import {
  getContext,
  call,
  put,
  takeLatest,
  select,
  all,
  delay,
  flush,
  race,
  take,
  SagaGenerator,
} from 'typed-redux-saga';
import type { PayloadAction } from '@reduxjs/toolkit';
import { followWithRetry } from '../utils/followWithRetry';
import { SCHEDULE_REFRESH_DEBOUNCE_MS } from '../utils/constants';
import type { SubscriptionFollowOutcome } from '../types/SubscriptionFollow.types';
import { RobotScheduleActions } from './robotScheduleSlice';
import type {
  AssociateDevicesToMapErrorAction,
  AssociateDevicesToMapRequestAction,
  AssociateDevicesToMapSuccessAction,
  CreateScheduleErrorAction,
  CreateScheduleRequestAction,
  CreateScheduleSuccessAction,
  FollowScheduleChangesAction,
  DeleteScheduleErrorAction,
  DeleteScheduleRequestAction,
  DeleteScheduleSuccessAction,
  ListAvailableMapGroupsErrorAction,
  ListAvailableMapGroupsRequestAction,
  ListAvailableMapGroupsSuccessAction,
  ListAvailableMapsErrorAction,
  ListAvailableMapsRequestAction,
  ListAvailableMapsSuccessAction,
  ListMapsErrorAction,
  ListMapsRequestAction,
  ListMapsSuccessAction,
  RobotScheduleListOccurrencesErrorAction,
  RobotScheduleListOccurrencesRequestAction,
  RobotScheduleListOccurrencesSuccessAction,
  RobotScheduleListRoutesErrorAction,
  RobotScheduleRefreshOccurrencesAction,
  RobotScheduleListRoutesRequestAction,
  RobotScheduleListRoutesSuccessAction,
  RobotScheduleListRoutinesErrorAction,
  RobotScheduleListRoutinesRequestAction,
  RobotScheduleListRoutinesSuccessAction,
  UpdateScheduleErrorAction,
  UpdateScheduleRequestAction,
  UpdateScheduleSuccessAction,
} from './robotScheduleActions.types';
import { selectRobotScheduleOccurrencesLastRequest } from './robotScheduleSelectors';
import type { IDependencies } from 'app/cross-cutting-concerns/dependency-injection/interfaces/IDependencies';
import type {
  AvailableMapsResponse,
  MapGroupsResponse,
  MapsResponse,
  RoutesResponse,
  RoutinesResponse,
  CleaningScheduleOccurrencesResponse,
} from 'app/modules/machine-inventory/ScheduleClient';
import type {
  AssociateDevicesToMapResponse,
  CleaningScheduleV2,
} from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import { toast } from 'app/cross-cutting-concerns/toasts/kcc/toast';

export function* listRoutinesSaga(
  action: RobotScheduleListRoutinesRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<RoutinesResponse>
  | PutEffect<RobotScheduleListRoutinesSuccessAction>
  | PutEffect<RobotScheduleListRoutinesErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listRoutines, action.payload);
    yield* put(RobotScheduleActions.robotScheduleListRoutinesSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.robotScheduleListRoutinesError({ error }));
  }
}

export function* listRoutesSaga(
  action: RobotScheduleListRoutesRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<RoutesResponse>
  | PutEffect<RobotScheduleListRoutesSuccessAction>
  | PutEffect<RobotScheduleListRoutesErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listRoutes, action.payload);
    yield* put(RobotScheduleActions.robotScheduleListRoutesSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.robotScheduleListRoutesError({ error }));
  }
}

export function* listMapsSaga(
  action: ListMapsRequestAction
): Generator<
  GetContextEffect | CallEffect<MapsResponse> | PutEffect<ListMapsSuccessAction> | PutEffect<ListMapsErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listMaps, action.payload);
    yield* put(RobotScheduleActions.listMapsSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.listMapsError({ error }));
  }
}

export function* listAvailableMapsSaga(
  action: ListAvailableMapsRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<AvailableMapsResponse>
  | PutEffect<ListAvailableMapsSuccessAction>
  | PutEffect<ListAvailableMapsErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listAvailableMaps, action.payload);
    yield* put(RobotScheduleActions.listAvailableMapsSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.listAvailableMapsError({ error }));
  }
}

export function* listAvailableMapGroupsSaga(
  action: ListAvailableMapGroupsRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<MapGroupsResponse>
  | PutEffect<ListAvailableMapGroupsSuccessAction>
  | PutEffect<ListAvailableMapGroupsErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listMapGroups, action.payload);
    yield* put(RobotScheduleActions.listAvailableMapGroupsSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.listAvailableMapGroupsError({ error }));
  }
}

export function* associateDevicesToMapSaga(
  action: AssociateDevicesToMapRequestAction
): Generator<
  | GetContextEffect
  | AllEffect<SagaGenerator<AssociateDevicesToMapResponse, CallEffect<AssociateDevicesToMapResponse>>>
  | PutEffect<AssociateDevicesToMapSuccessAction>
  | PutEffect<AssociateDevicesToMapErrorAction>
  | PutEffect<ListMapsRequestAction>,
  void,
  IDependencies
> {
  const { scheduleService, t } = yield* getContext<IDependencies>('dependencies');
  const { mapIds, materialNumber, serialNumber } = action.payload;

  try {
    const responses = yield* all(
      mapIds.map(mapId =>
        call(scheduleService.associateDevicesToMap, {
          id: mapId,
          associatedDevices: [{ materialNumber, serialNumber }],
        })
      )
    );

    const success = responses.every(response => response.success);

    if (!success) {
      throw new Error('Failed to associate devices to map');
    }

    yield* put(RobotScheduleActions.associateDevicesToMapSuccess({ success }));
    yield* put(
      RobotScheduleActions.listMapsRequest({ mnsns: [{ materialNumber, serialNumber }], filter: { limit: 10000 } })
    );

    toast.success({
      message: undefined,
      description: t('mapAssignmentModal.assignSuccessMessage') as string,
      closable: true,
    });
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.associateDevicesToMapError({ error }));

    toast.error({
      message: t('mapAssignmentModal.associateErrorMessage') as string,
      description: (error as Error).message,
    });
  }
}

export function* listOccurrencesSaga(
  action: RobotScheduleListOccurrencesRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<CleaningScheduleOccurrencesResponse>
  | PutEffect<RobotScheduleListOccurrencesSuccessAction>
  | PutEffect<RobotScheduleListOccurrencesErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listCleaningScheduleOccurrences, action.payload);
    yield* put(RobotScheduleActions.robotScheduleListOccurrencesSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.robotScheduleListOccurrencesError({ error }));
  }
}

// Refetches without touching the loading state; drops the result if the visible period changed meanwhile.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* refreshOccurrencesSaga(action: RobotScheduleRefreshOccurrencesAction): Generator<unknown, void, any> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listCleaningScheduleOccurrences, action.payload);
    const lastRequest = yield* select(selectRobotScheduleOccurrencesLastRequest);

    if (
      lastRequest?.materialNumber === action.payload.materialNumber &&
      lastRequest?.serialNumber === action.payload.serialNumber &&
      lastRequest?.period.startAt === action.payload.period.startAt &&
      lastRequest?.period.endAt === action.payload.period.endAt
    ) {
      yield* put(RobotScheduleActions.robotScheduleRefreshOccurrencesSuccess(response));
    } else {
      yield* put(RobotScheduleActions.robotScheduleRefreshOccurrencesDone());
    }
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.robotScheduleRefreshOccurrencesDone());
  }
}

// A list request started after this refresh is newer, so it supersedes the refresh.
export function* refreshUnlessListRequestedSaga(
  action: RobotScheduleRefreshOccurrencesAction
): Generator<unknown, void, unknown> {
  yield* race({
    refresh: call(refreshOccurrencesSaga, action),
    superseded: take(RobotScheduleActions.robotScheduleListOccurrencesRequest),
  });
}

type ScheduleChangesChannelEvent = { kind: 'data' } | { kind: 'error'; error: unknown };

// SagaUtils.createChannelFromObservable only logs errors; forward them so a dropped socket is retried.
// Sliding(1) keeps one pending event while a refresh runs, so nothing is lost and bursts collapse.
const createScheduleChangesChannel = (
  subscribeToScheduleChanged: IDependencies['scheduleService']['subscribeToScheduleChanged'],
  materialNumber: string,
  serialNumber: string
): EventChannel<ScheduleChangesChannelEvent> =>
  eventChannel<ScheduleChangesChannelEvent>(emit => {
    const subscription = subscribeToScheduleChanged({ materialNumber, serialNumber }).subscribe({
      next: result => {
        if (result.data?.scheduleChanged) {
          emit({ kind: 'data' });
        }
      },
      error: error => emit({ kind: 'error', error }),
      complete: () => emit({ kind: 'error', error: new Error('scheduleChanged subscription completed') }),
    });

    return () => subscription.unsubscribe();
  }, buffers.sliding(1));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* followScheduleChanges(
  scheduleService: IDependencies['scheduleService'],
  materialNumber: string,
  serialNumber: string,
  isResubscribe: boolean
): Generator<unknown, SubscriptionFollowOutcome, any> {
  const channel = yield* call(
    createScheduleChangesChannel,
    scheduleService.subscribeToScheduleChanged,
    materialNumber,
    serialNumber
  );

  try {
    // Events published while the socket was down are lost, so catch up once it is back.
    if (isResubscribe) {
      const lastRequest = yield* select(selectRobotScheduleOccurrencesLastRequest);

      if (lastRequest) {
        yield* put(RobotScheduleActions.robotScheduleRefreshOccurrences(lastRequest));
      }
    }

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { event, stopped } = yield* race({
        stopped: take(RobotScheduleActions.stopFollowingScheduleChanges),
        event: take(channel),
      });

      if (stopped || !event) {
        return { kind: 'stopped' };
      }

      if (event.kind === 'error') {
        console.error(event.error);

        return { kind: 'failed', error: event.error };
      }

      const { stopped: stoppedWhileWaiting } = yield* race({
        stopped: take(RobotScheduleActions.stopFollowingScheduleChanges),
        elapsed: delay(SCHEDULE_REFRESH_DEBOUNCE_MS),
      });

      if (stoppedWhileWaiting) {
        return { kind: 'stopped' };
      }

      // The refresh below covers everything published during the wait.
      const buffered = (yield* flush(channel)) as ScheduleChangesChannelEvent[];

      const bufferedError = buffered.find(bufferedEvent => bufferedEvent.kind === 'error');

      if (bufferedError?.kind === 'error') {
        console.error(bufferedError.error);

        return { kind: 'failed', error: bufferedError.error };
      }

      const lastRequest = yield* select(selectRobotScheduleOccurrencesLastRequest);

      if (lastRequest) {
        yield* put(RobotScheduleActions.robotScheduleRefreshOccurrences(lastRequest));
      }
    }
  } finally {
    channel.close();
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* followScheduleChangesSaga(action: FollowScheduleChangesAction): Generator<unknown, void, any> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');
  const { materialNumber, serialNumber } = action.payload;

  let hasFollowed = false;

  yield* followWithRetry({
    follow: () => {
      const isResubscribe = hasFollowed;
      hasFollowed = true;

      return followScheduleChanges(scheduleService, materialNumber, serialNumber, isResubscribe);
    },
    stop: RobotScheduleActions.stopFollowingScheduleChanges,
  });
}

export function* createScheduleSaga(
  action: CreateScheduleRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<CleaningScheduleV2 | void>
  | SelectEffect
  | PutEffect<CreateScheduleSuccessAction>
  | PutEffect<CreateScheduleErrorAction>
  | PutEffect<RobotScheduleListOccurrencesRequestAction>
  | PutEffect<PayloadAction>,
  void,
  IDependencies
> {
  const { scheduleService, t } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.createSchedule, action.payload);
    yield* put(RobotScheduleActions.createScheduleSuccess(response));

    toast.success({
      message: t('createSchedule.scheduleCreatedMessage') as string,
      description: t('createSchedule.scheduleCreatedDescription') as string,
    });

    const lastOccurrencesRequest = yield* select(selectRobotScheduleOccurrencesLastRequest);

    if (lastOccurrencesRequest) {
      yield* put(RobotScheduleActions.robotScheduleListOccurrencesRequest(lastOccurrencesRequest));
    }
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.createScheduleError({ error }));

    toast.error({
      message: t('createSchedule.scheduleCreatedError') as string,
      description: (error as Error).message,
    });
  }
}

export function* updateScheduleSaga(
  action: UpdateScheduleRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<CleaningScheduleV2 | void>
  | SelectEffect
  | PutEffect<UpdateScheduleSuccessAction>
  | PutEffect<UpdateScheduleErrorAction>
  | PutEffect<RobotScheduleListOccurrencesRequestAction>
  | PutEffect<PayloadAction>,
  void,
  IDependencies
> {
  const { scheduleService, t } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.updateSchedule, action.payload);
    yield* put(RobotScheduleActions.updateScheduleSuccess(response));

    toast.success({
      message: t('createSchedule.scheduleUpdatedMessage') as string,
      description: t('createSchedule.scheduleUpdatedDescription') as string,
    });

    const lastOccurrencesRequest = yield* select(selectRobotScheduleOccurrencesLastRequest);

    if (lastOccurrencesRequest) {
      yield* put(RobotScheduleActions.robotScheduleListOccurrencesRequest(lastOccurrencesRequest));
    }
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.updateScheduleError({ error }));

    toast.error({
      message: t('createSchedule.scheduleUpdatedError') as string,
      description: (error as Error).message,
    });
  }
}

export function* deleteScheduleSaga(
  action: DeleteScheduleRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<CleaningScheduleV2>
  | SelectEffect
  | PutEffect<DeleteScheduleSuccessAction>
  | PutEffect<DeleteScheduleErrorAction>
  | PutEffect<RobotScheduleListOccurrencesRequestAction>,
  void,
  IDependencies
> {
  const { scheduleService, t } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.deleteSchedule, action.payload);
    yield* put(RobotScheduleActions.deleteScheduleSuccess(response));

    toast.success({
      message: t('createSchedule.scheduleDeletedMessage') as string,
      description: t('createSchedule.scheduleDeletedDescription') as string,
    });

    const lastOccurrencesRequest = yield* select(selectRobotScheduleOccurrencesLastRequest);

    if (lastOccurrencesRequest) {
      yield* put(RobotScheduleActions.robotScheduleListOccurrencesRequest(lastOccurrencesRequest));
    }
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.deleteScheduleError({ error }));

    toast.error({
      message: t('createSchedule.scheduleDeletedError') as string,
      description: (error as Error).message,
    });
  }
}

export function* robotScheduleSaga(): Generator<ForkEffect<never>, void> {
  yield* takeLatest(RobotScheduleActions.robotScheduleListRoutinesRequest, listRoutinesSaga);
  yield* takeLatest(RobotScheduleActions.robotScheduleListRoutesRequest, listRoutesSaga);
  yield* takeLatest(RobotScheduleActions.listMapsRequest, listMapsSaga);
  yield* takeLatest(RobotScheduleActions.robotScheduleListOccurrencesRequest, listOccurrencesSaga);
  yield* takeLatest(RobotScheduleActions.robotScheduleRefreshOccurrences, refreshUnlessListRequestedSaga);
  yield* takeLatest(RobotScheduleActions.followScheduleChanges, followScheduleChangesSaga);
  yield* takeLatest(RobotScheduleActions.createScheduleRequest, createScheduleSaga);
  yield* takeLatest(RobotScheduleActions.updateScheduleRequest, updateScheduleSaga);
  yield* takeLatest(RobotScheduleActions.deleteScheduleRequest, deleteScheduleSaga);
  yield* takeLatest(RobotScheduleActions.listAvailableMapsRequest, listAvailableMapsSaga);
  yield* takeLatest(RobotScheduleActions.listAvailableMapGroupsRequest, listAvailableMapGroupsSaga);
  yield* takeLatest(RobotScheduleActions.associateDevicesToMapRequest, associateDevicesToMapSaga);
}
